/**
 * Thumbs up/down feedback UI for the AG-UI assistant.
 *
 * Renders inline in the assistant action bar area after the last assistant
 * message. Shows a popover with an optional text input when a thumb is clicked.
 */
import { useState } from "react";
import { ThumbsDown, ThumbsUp, X } from "lucide-react";
import type { MessageFeedbackCreate } from "~/components/assistant/types";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Textarea } from "~/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

interface MessageFeedbackProps {
  feedback: MessageFeedbackCreate | null;
  isSubmitting: boolean;
  onSubmit: (value: 0 | 1, text?: string | null) => Promise<void>;
  onClear?: () => Promise<void>;
}

export function MessageFeedbackInput({
  feedback,
  isSubmitting,
  onSubmit,
  onClear,
}: MessageFeedbackProps) {
  const [openPopover, setOpenPopover] = useState<"up" | "down" | null>(null);
  const [textFeedback, setTextFeedback] = useState("");

  const currentValue = feedback?.feedback_value ?? null;

  const handleThumbClick = (thumbType: "up" | "down") => {
    if (openPopover === thumbType) {
      setOpenPopover(null);
      return;
    }
    setOpenPopover(thumbType);
    setTextFeedback(feedback?.text || "");
  };

  const handleSubmit = async () => {
    if (isSubmitting || !openPopover) return;

    const feedbackValue = openPopover === "up" ? 1 : 0;
    await onSubmit(feedbackValue as 0 | 1, textFeedback.trim() || null);
    setOpenPopover(null);
    setTextFeedback("");
  };

  const handleClear = async () => {
    if (isSubmitting) return;
    await onClear?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const popoverContent = (thumbType: "up" | "down") => (
    <div className="flex flex-col gap-3">
      <div className="font-medium text-sm">Feedback</div>
      <div className="flex flex-col gap-1.5">
        <label className="text-xs text-gray-600">Provide Details: (optional)</label>
        <Textarea
          placeholder={
            thumbType === "up"
              ? "Tell us what you liked about this response"
              : "Tell us what you didn't like about this response"
          }
          value={textFeedback}
          onChange={(e) => setTextFeedback(e.target.value)}
          onKeyDown={handleKeyDown}
          className="min-h-[80px]"
          disabled={isSubmitting}
        />
      </div>
      <p className="text-xs text-gray-600">
        Your feedback will make this entire conversation visible to administrators.
      </p>
      <Button onClick={handleSubmit} disabled={isSubmitting} size="sm">
        Submit
      </Button>
    </div>
  );

  return (
    <div className="flex items-center gap-1">
      {/* Thumbs Up */}
      <Popover
        open={openPopover === "up"}
        onOpenChange={(open) => {
          if (!open) setOpenPopover(null);
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                onClick={() => handleThumbClick("up")}
                disabled={isSubmitting}
                className={cn(
                  "p-1 rounded transition-colors text-muted-foreground",
                  "hover:bg-muted cursor-pointer disabled:opacity-50",
                  currentValue === 1 && "text-foreground"
                )}
                aria-label="Helpful"
                aria-pressed={currentValue === 1}
              >
                <ThumbsUp className="size-4" fill={currentValue === 1 ? "currentColor" : "none"} />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Helpful</p>
          </TooltipContent>
        </Tooltip>
        <PopoverContent className="w-80" onCloseAutoFocus={(e) => e.preventDefault()}>
          {popoverContent("up")}
        </PopoverContent>
      </Popover>

      {/* Thumbs Down */}
      <Popover
        open={openPopover === "down"}
        onOpenChange={(open) => {
          if (!open) setOpenPopover(null);
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <button
                onClick={() => handleThumbClick("down")}
                disabled={isSubmitting}
                className={cn(
                  "p-1 rounded transition-colors text-muted-foreground",
                  "hover:bg-muted cursor-pointer disabled:opacity-50",
                  currentValue === 0 && "text-foreground"
                )}
                aria-label="Not helpful"
                aria-pressed={currentValue === 0}
              >
                <ThumbsDown
                  className="size-4"
                  fill={currentValue === 0 ? "currentColor" : "none"}
                />
              </button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Not helpful</p>
          </TooltipContent>
        </Tooltip>
        <PopoverContent className="w-80" onCloseAutoFocus={(e) => e.preventDefault()}>
          {popoverContent("down")}
        </PopoverContent>
      </Popover>

      {/* Clear button — only when feedback exists and delete is supported */}
      {feedback && onClear && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleClear}
              disabled={isSubmitting}
              className={cn(
                "p-1 rounded transition-colors text-muted-foreground",
                "hover:bg-muted cursor-pointer disabled:opacity-50"
              )}
              aria-label="Clear feedback"
            >
              <X className="size-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Clear feedback</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
